/**
 * Logger Utility
 * Logs condicionais que são completamente removidos em produção via tree-shaking
 */

/// <reference types="vite/client" />

// Flag de desenvolvimento - será substituída em build-time
const isDev = import.meta.env.DEV

/**
 * Logger condicional que só executa em desenvolvimento
 * Em produção, essas chamadas são completamente removidas do bundle via tree-shaking
 */
export const logger = {
    /**
     * Log informativo (equivalente a console.log)
     */
    log: (...args: any[]) => {
        if (isDev) {
            console.log(...args)
        }
    },

    /**
     * Log de informação (equivalente a console.info)
     */
    info: (...args: any[]) => {
        if (isDev) {
            console.info(...args)
        }
    },

    /**
     * Log de aviso (equivalente a console.warn)
     */
    warn: (...args: any[]) => {
        if (isDev) {
            console.warn(...args)
        }
    },

    /**
     * Log de erro (equivalente a console.error)
     * NOTA: Erros são mantidos em produção para debugging
     */
    error: (...args: any[]) => {
        console.error(...args)
    },

    /**
     * Log de debug (equivalente a console.debug)
     */
    debug: (...args: any[]) => {
        if (isDev) {
            console.debug(...args)
        }
    },

    /**
     * Log de tabela (equivalente a console.table)
     */
    table: (data: any) => {
        if (isDev) {
            console.table(data)
        }
    },

    /**
     * Agrupa logs (equivalente a console.group)
     */
    group: (label: string) => {
        if (isDev) {
            console.group(label)
        }
    },

    /**
     * Agrupa logs colapsado (equivalente a console.groupCollapsed)
     */
    groupCollapsed: (label: string) => {
        if (isDev) {
            console.groupCollapsed(label)
        }
    },

    /**
     * Finaliza grupo (equivalente a console.groupEnd)
     */
    groupEnd: () => {
        if (isDev) {
            console.groupEnd()
        }
    },

    /**
     * Limpa console (equivalente a console.clear)
     */
    clear: () => {
        if (isDev) {
            console.clear()
        }
    },

    /**
     * Marca tempo (equivalente a console.time)
     */
    time: (label: string) => {
        if (isDev) {
            console.time(label)
        }
    },

    /**
     * Finaliza marca de tempo (equivalente a console.timeEnd)
     */
    timeEnd: (label: string) => {
        if (isDev) {
            console.timeEnd(label)
        }
    },

    /**
     * Conta ocorrências (equivalente a console.count)
     */
    count: (label: string) => {
        if (isDev) {
            console.count(label)
        }
    },

    /**
     * Reseta contador (equivalente a console.countReset)
     */
    countReset: (label: string) => {
        if (isDev) {
            console.countReset(label)
        }
    },
}

/**
 * Função auxiliar para criar logs com prefixo
 * Útil para identificar a origem dos logs
 */
export const createLogger = (prefix: string) => ({
    log: (...args: any[]) => logger.log(`[${prefix}]`, ...args),
    info: (...args: any[]) => logger.info(`[${prefix}]`, ...args),
    warn: (...args: any[]) => logger.warn(`[${prefix}]`, ...args),
    error: (...args: any[]) => logger.error(`[${prefix}]`, ...args),
    debug: (...args: any[]) => logger.debug(`[${prefix}]`, ...args),
})
